import { DEFAULT_LOCALE, LOCALE_CONFIG, isSupportedLocale } from '@erp/shared';

function localeTag(language: string): string {
  const normalized = language.trim().toLocaleLowerCase('en-US');
  const baseLocale = normalized.split('-')[0];
  const locale = isSupportedLocale(normalized)
    ? normalized
    : baseLocale !== undefined && isSupportedLocale(baseLocale)
      ? baseLocale
      : DEFAULT_LOCALE;
  return LOCALE_CONFIG[locale].intlLocale;
}

export function formatDate(value: string | null, language: string): string {
  if (value === null) {
    return '—';
  }
  return new Intl.DateTimeFormat(localeTag(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

export function formatDateTime(value: string | null, language: string): string {
  if (value === null) {
    return '—';
  }
  return new Intl.DateTimeFormat(localeTag(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export function formatMoney(value: string | null, currency: string, language: string): string {
  if (value === null) {
    return '—';
  }
  const numeric = Number.parseFloat(value);
  if (Number.isNaN(numeric)) {
    return '—';
  }
  return new Intl.NumberFormat(localeTag(language), {
    style: 'currency',
    currency,
  }).format(numeric);
}

export function formatQuantity(
  value: string | null,
  language: string,
  maximumFractionDigits = 3,
): string {
  if (value === null) {
    return '—';
  }
  const numeric = Number.parseFloat(value);
  if (Number.isNaN(numeric)) {
    return '—';
  }
  return new Intl.NumberFormat(localeTag(language), {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(numeric);
}

export function formatPercent(value: string | null, language: string): string {
  if (value === null) return '—';
  const numeric = Number.parseFloat(value);
  if (Number.isNaN(numeric)) {
    return '—';
  }
  return new Intl.NumberFormat(localeTag(language), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numeric / 100);
}

export function formatDuration(seconds: number | null, language: string): string {
  if (seconds === null) {
    return '—';
  }
  const minutes = Math.round(seconds / 60);
  return new Intl.NumberFormat(localeTag(language)).format(minutes);
}

/** Converts an ISO timestamp into the value format of `<input type="datetime-local">`. */
export function toDateTimeLocal(value: Date): string {
  const offsetMs = value.getTimezoneOffset() * 60_000;
  return new Date(value.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function fromDateTimeLocal(value: string): string {
  return new Date(value).toISOString();
}
